import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { env } from "@/config/env";

export const NOTIFICATION_CONTENT_MAX_LINES = 5;

const CATEGORY_TITLES: Record<string, string> = {
  "comment-added": "Comment added",
  "i-am-mentioned": "You were mentioned",
  "request-created": "Request created",
  "request-updated": "Request updates",
};

const TAG_FALLBACK_TITLES: Record<string, string> = {
  mention: "You were mentioned",
  mentions: "You were mentioned",
  request: "Request updates",
};

export function getNotificationTitle(category: string, tag: string): string {
  return (
    CATEGORY_TITLES[category] ?? TAG_FALLBACK_TITLES[tag] ?? "Notification"
  );
}

export function formatActionLink(link: string): string {
  const absoluteLink =
    link.startsWith("http") || link.startsWith("/") ? link : `https://${link}`;
  const { hash, pathname, search } = new URL(absoluteLink, env.VITE_BASE_URL);

  return new URL(`${pathname}${search}${hash}`, env.VITE_BASE_URL).toString();
}

export function NotificationContentLines({
  content,
  maxLines,
}: {
  content: string;
  maxLines?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const lines = content.split("\n").filter((line) => line.trim() !== "");
  const hasMore = Boolean(maxLines) && lines.length > maxLines!;
  const collapsedLines = hasMore ? lines.slice(0, maxLines) : lines;
  const extraLines = hasMore ? lines.slice(maxLines) : [];

  return (
    <>
      <ul className="list-disc list-inside text-text-tertiary font-normal leading-normal">
        <p dangerouslySetInnerHTML={{ __html: collapsedLines[0] ?? "" }} />
        {collapsedLines.slice(1).map((line, index) => (
          <li
            className="pl-4"
            dangerouslySetInnerHTML={{ __html: line.replace(/^- /, "") }}
            key={index}
          />
        ))}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              style={{ overflow: "hidden" }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              {extraLines.map((line, index) => (
                <li
                  className="pl-4"
                  dangerouslySetInnerHTML={{
                    __html: line.replace(/^- /, ""),
                  }}
                  key={index}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </ul>
      {hasMore && (
        <button
          className="mt-1 flex items-center gap-1 text-xs text-fg-brand-primary-alt hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            className="flex items-center gap-1"
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={13} />
          </motion.span>
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}
